import type { CreateJobInput } from '@chukua/common';
import { FileX, ShieldAlert } from 'lucide-react';
import { Navigate, useParams } from 'react-router';
import { DetailPageSkeleton } from '../../components/DetailPageSkeleton';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { sectionTrail } from '../../layouts/section-trail';
import { JobAttachments } from './JobAttachments';
import { JobIntakeForm } from './JobIntakeForm';
import { JobIntakeMissingItems } from './JobIntakeMissingItems';
import { useJobCatalogues } from './use-job-catalogues';
import { useJobRecord } from './use-job-record';

const JOBS_TRAIL = sectionTrail('jobs');

/** `/jobs` (quick-create) and `/jobs/:id` (reopen to finish or check). Keyed by id, so moving between jobs starts fresh. */
export function JobIntakePage() {
  const { id } = useParams();
  return <JobIntake key={id ?? 'new'} jobId={id} />;
}

function JobIntake({ jobId }: { jobId: string | undefined }) {
  const { state, saveError, saving, create, update, reload } = useJobRecord(jobId);
  const catalogues = useJobCatalogues();

  if (state.status === 'forbidden') return <Navigate to="/" replace />;
  if (state.status === 'loading') return <DetailPageSkeleton label="Loading job" breadcrumbs={JOBS_TRAIL} tabs={0} />;
  if (state.status === 'not_found') {
    return <EmptyState icon={FileX} title="Job not found" description="This job doesn't exist, or the link is out of date." />;
  }
  if (state.status === 'error') {
    return <EmptyState icon={ShieldAlert} title="Couldn't load this job" description="Something went wrong loading this job. Try reloading the page." />;
  }

  const { job } = state;
  const jobTypes = catalogues.status === 'loaded' ? catalogues.jobTypes : [];
  const zones = catalogues.status === 'loaded' ? catalogues.zones : [];

  async function handleSave(input: CreateJobInput): Promise<void> {
    if (job) await update(input);
    else await create(input);
  }

  return (
    <>
      <PageHeader
        breadcrumbs={JOBS_TRAIL}
        title={job ? job.number : 'New job'}
        description="Customer and job type are enough to start — everything else can be filled in as you learn it."
      />
      {job && <JobIntakeMissingItems job={job} />}
      <JobIntakeForm job={job} jobTypes={jobTypes} zones={zones} error={saveError} saving={saving} onSave={handleSave} />
      {job && <JobAttachments jobId={job.id} attachments={job.attachments} onUploaded={reload} />}
    </>
  );
}
